import React, { useState, useEffect } from "react";
import * as style from "./CreateEditRoleSidePanel.module.scss";
import {
  Divider,
  Dropdown,
  DropdownOption,
} from "cprime-foundational-components";
import Typography from "../Common/CprimeText";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import {
  updateEditApiRelationsPayload,
  updateCreateApiRelationsPayload,
  selectCurrentRoleId,
} from "../../store/slices/CreateRoleSlice";
import { getAPIDetails } from "../../services/RequestAccess/RequestAccess";

interface CreateEditRoleSidePanelProps {
  tableData: any[];
  isEdit: boolean;
  clickedRowId: any;
}

const CreateEditRoleSidePanel: React.FC<CreateEditRoleSidePanelProps> = ({
  tableData,
  isEdit,
  clickedRowId,
}) => {
  const scssClass = style as any;
  const [selectedAPI, setSelectedAPI] = useState<any | null>(null);
  const [methodType, setMethodType] = useState<DropdownOption[]>([]);
  const [access, setAccess] = useState<DropdownOption | null>(null);
  const [apiTableData, setApiTableData] = useState<any[]>(tableData);
  const { data } = useSelector((state: RootState) => state.applicationDetails);
  const currentRoleId = useSelector(selectCurrentRoleId);
  const dispatch = useDispatch();

  const allMethodTypes = ["POST", "GET", "PUT", "DELETE", "PATCH", "ANY"];
  const allAccessTypes = ["Allow", "Deny"];

  const apiNameOptions: DropdownOption[] = apiTableData.map((item) => ({
    id: item.id,
    label: item.api_name,
    value: item.id.toString(),
  }));

  const getMethodTypeOptions = (): DropdownOption[] => {
    return allMethodTypes.map((method, index) => ({
      id: index + 1,
      label: method,
      value: method,
    }));
  };

  const getAccessOptions = (): DropdownOption[] => {
    return allAccessTypes.map((access, index) => ({
      id: index + 1,
      label: access,
      value: access.toLowerCase(),
    }));
  };

  const getApiDetailsWithAppId = async () => {
    try {
      const res = await getAPIDetails(data?.id);
      setApiTableData(res?.data?.rows);
      return res.data.rows;
    } catch (err) {
      // console.error("Error fetching API details with appId", err);
    }
  };

  useEffect(() => {
    if (!isEdit) {
      getApiDetailsWithAppId();
    }
  }, [isEdit, data?.id]);

  useEffect(() => {
    if (isEdit && clickedRowId && apiTableData?.length > 0) {
      const selectedRow = apiTableData?.find(
        (item) => item?.id?.toString() === clickedRowId?.toString(),
      );
      if (selectedRow) {
        setSelectedAPI(selectedRow);

        const methodOptions = getMethodTypeOptions();
        const accessOpts = getAccessOptions();

        const methodTypes =
          selectedRow?.method_type?.toUpperCase()?.split(", ") || [];
        const methods = methodTypes
          ?.map((type: string) =>
            methodOptions?.find((opt) => opt?.value === type?.toUpperCase()),
          )
          ?.filter(Boolean) as DropdownOption[];

        const firstAccess = selectedRow?.access?.split(", ")[0];
        const acc =
          accessOpts?.find(
            (opt) => opt?.value === firstAccess?.toLowerCase(),
          ) || null;

        setMethodType(methods);
        setAccess(acc);
      }
    }
  }, [isEdit, clickedRowId, apiTableData]);

  const handleAPIChange = (selectedOptions: DropdownOption[]) => {
    const selectedOption = selectedOptions[0];
    const selected = apiTableData?.find(
      (item) => item?.id?.toString() === selectedOption?.value,
    );
    setSelectedAPI(selected);

    const methodOptions = getMethodTypeOptions();
    const accessOpts = getAccessOptions();

    const methodTypes = selected?.method_type?.toUpperCase()?.split(", ") || [];
    const methods = methodTypes
      ?.map((type: any) =>
        methodOptions?.find((opt) => opt?.value === type?.toUpperCase()),
      )
      ?.filter(Boolean) as DropdownOption[];

    const firstAccess = selected?.access?.split(", ")[0];
    const acc =
      accessOpts?.find((opt) => opt?.value === firstAccess?.toLowerCase()) ||
      null;

    setMethodType(methods);
    setAccess(acc);
  };

  const handleAccessChange = (selected: DropdownOption[]) => {
    const selectedAccess = selected?.[0];
    setAccess(selectedAccess);
  };

  const accessOptions = getAccessOptions();

  useEffect(() => {
    if (selectedAPI) {
      const currentSelections = {
        id: selectedAPI?.id,
        apiName: selectedAPI?.api_name,
        endpointPath: selectedAPI?.endpoint_path,
        description: selectedAPI?.description,
        methodType: methodType?.map((m) => m?.value).join(", "),
        access: access?.value?.toString() || "",
        appId: data?.id,
      };
      dispatch(updateEditApiRelationsPayload(currentSelections));

      if (!isEdit && currentRoleId) {
        const currentCreateSelections = {
          id: selectedAPI.id,
          methodType: methodType?.map((m) => m.value).join(", "),
          access: access?.value?.toString() || "",
          appId: data?.id,
          roleId: currentRoleId,
        };
        dispatch(updateCreateApiRelationsPayload(currentCreateSelections));
      }
    }
  }, [selectedAPI, methodType, access]);

  return (
    <div className={scssClass.panelContent}>
      <Divider
        variant={"fullWidth"}
        sx={{
          marginBottom: "15px",
          width: "100%",
          marginLeft: "0px !important",
          backgroundColor: "#f8f8f9 !important",
        }}
      />
      <Typography className={scssClass.textDescription}>API Details</Typography>

      <div className={scssClass.fieldsContent}>
        {/* API Name */}
        <div>
          <Typography className={scssClass.label}>API Name</Typography>
          <Dropdown
            placeholder="Select"
            optionsTextLimit={200}
            fullWidth
            style={{ width: "100%" }}
            multiple={false}
            options={apiNameOptions}
            selectedOptions={
              selectedAPI
                ? [
                    {
                      id: selectedAPI?.id,
                      label: selectedAPI?.api_name,
                      value: selectedAPI?.id?.toString(),
                    },
                  ]
                : []
            }
            onChange={handleAPIChange}
          />
        </div>

        {/* API Endpoint (read-only) */}
        <div>
          <Typography className={scssClass.label}>API Endpoint</Typography>
          <Dropdown
            placeholder="Select"
            optionsTextLimit={200}
            fullWidth
            style={{ width: "100%" }}
            multiple={false}
            disabled
            options={
              selectedAPI
                ? [
                    {
                      id: selectedAPI.id,
                      label: selectedAPI.endpoint_path,
                      value: selectedAPI.endpoint_path,
                    },
                  ]
                : []
            }
            selectedOptions={
              selectedAPI
                ? [
                    {
                      id: selectedAPI.id,
                      label: selectedAPI.endpoint_path,
                      value: selectedAPI.endpoint_path,
                    },
                  ]
                : []
            }
            onChange={() => {}}
          />
        </div>

        {/* API Description (read-only) */}
        <div>
          <Typography className={scssClass.label}>API Description</Typography>
          <Dropdown
            placeholder="Select"
            optionsTextLimit={200}
            fullWidth
            style={{ width: "100%" }}
            multiple={false}
            disabled
            options={
              selectedAPI
                ? [
                    {
                      id: selectedAPI.id,
                      label: selectedAPI.description,
                      value: selectedAPI.description,
                    },
                  ]
                : []
            }
            selectedOptions={
              selectedAPI
                ? [
                    {
                      id: selectedAPI.id,
                      label: selectedAPI.description,
                      value: selectedAPI.description,
                    },
                  ]
                : []
            }
            onChange={() => {}}
          />
        </div>

        {/* Method Type (Multi-Select) */}
        <div>
          <Typography className={scssClass.label}>Method Type</Typography>
          <Dropdown
            placeholder="Select"
            optionsTextLimit={200}
            fullWidth
            style={{ width: "100%" }}
            //type={DropdownType.MultiSelect}
            options={methodType}
            selectedOptions={methodType}
            disabled
            onChange={() => {}}
          />
        </div>

        {/* Access */}
        <div>
          <Typography className={scssClass.label}>Access</Typography>
          <Dropdown
            placeholder="Select"
            optionsTextLimit={200}
            style={{ width: "100%" }}
            fullWidth
            multiple={false}
            options={accessOptions}
            selectedOptions={access ? [access] : []}
            onChange={handleAccessChange}
          />
        </div>
      </div>
    </div>
  );
};

export default CreateEditRoleSidePanel;
